import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { LayoutGrid, Table, Plus } from 'lucide-react';
import { Button } from '../../../ui/components/Common/Buttons';
import { FilterSearch } from '../../../ui/components/Common/FilterSearch/FilterSearch';
import { ORGANIZATION_FILTER_OPTIONS } from '../constants';
import type { OrganizationFilters, OrganizationViewMode } from '../interfaces';

interface IOrganizationsHeaderProps {
  viewMode: OrganizationViewMode;
  onViewModeChange: (mode: OrganizationViewMode) => void;
  onSearch: (query: string) => void;
  onFiltersChange: (filters: OrganizationFilters) => void;
  onCreate: () => void;
}

export const OrganizationsHeader: React.FC<IOrganizationsHeaderProps> = ({
  viewMode,
  onViewModeChange,
  onSearch,
  onFiltersChange,
  onCreate,
}) => {
  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: OrganizationViewMode | null,
  ) => {
    if (newMode !== null) {
      onViewModeChange(newMode);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { md: 'center' },
        justifyContent: 'space-between',
        gap: 3,
        mb: 5,
      }}
    >
      <Box>
        <Typography
          variant="h4"
          sx={{ fontWeight: 900, color: 'text.primary', letterSpacing: '-0.03em', mb: 0.5 }}
        >
          Active Tenants
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          Manage and monitor all tenant organizations and domains.
        </Typography>
      </Box>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Box sx={{ position: 'relative' }}>
          <FilterSearch
            filterOptions={ORGANIZATION_FILTER_OPTIONS}
            onSearch={onSearch}
            onApplyFilters={onFiltersChange}
            searchText="Search by organization name or domain..."
            hasFilters={true}
          />
        </Box>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.75rem',
              px: 1.5,
              py: 0.75,
              gap: 0.75,
              borderRadius: 3,
            },
          }}
        >
          <ToggleButton value="grid">
            <LayoutGrid size={15} />
            Grid
          </ToggleButton>
          <ToggleButton value="table">
            <Table size={15} />
            Table
          </ToggleButton>
        </ToggleButtonGroup>

        <Button
          label="New Organization"
          hasIcon
          variant="primary"
          icon={<Plus size={18} />}
          onClick={onCreate}
        />
      </Stack>
    </Box>
  );
};
